import React from 'react';
import {
  Button,
  ButtonColor,
  ButtonSize,
  Header4,
  IconType,
  List,
  SimpleBlock,
  Text,
  TextLink,
  useShowContextRequired,
} from 'ui-builder';
import {
  ADDITIONAL_PROJECT_PLAN_APPLIED,
  ADDITIONAL_PROJECT_PLAN_CREATED,
  ProjectPlan,
  useDateHelper,
  useProjectPlanService,
} from 'nekst-api';
import {
  RowRepeater,
  Separator,
  TextBadge,
  TextBadgeVariant,
  usePopupFormManager,
} from 'features/nekst-widgets';
import WorkflowSelectionModal
  from '../launch/additionalWorkflow/WorkflowSelectionModal';
import styles from './AppliedWorkflowsBlock.module.scss';

type AppliedProjectPlan = ProjectPlan & { appliedAt: Date };

function WorkflowLinks() {
  const { data } = useShowContextRequired<AppliedProjectPlan>();

  return (
    <span>
      {data._sourcePlans.map((sp, index) => (
        <React.Fragment key={sp.id}>
          {index > 0 && ', '}
          <TextLink href={`/plans/${sp.id}`} target="_blank">
            {sp.name}
          </TextLink>
        </React.Fragment>
      ))}
    </span>
  );
}

function AppliedWorkflowCard() {
  const { data } = useShowContextRequired<AppliedProjectPlan>();
  const dateHelper = useDateHelper();

  return (
    <div className={styles.card}>
      <WorkflowLinks />
      <div className={styles.badges}>
        {data.appliedTasksCount != null && (
          <TextBadge
            text={`${data.appliedTasksCount} tasks`}
            isSmall
          />
        )}
        {data.deleteExistingTasks && (
          <TextBadge
            text="Tasks replaced"
            variant={TextBadgeVariant.WARNING}
            isSmall
          />
        )}
      </div>
      <Text>
        <span className={styles.date}>
          Applied: {dateHelper.toString(data.appliedAt)}
        </span>
      </Text>
    </div>
  );
}

function AddWorkflowButton(props: { projectId: number }) {
  const popupFormManager = usePopupFormManager();

  return (
    <Button
      text="Add"
      size={ButtonSize.SMALL}
      color={ButtonColor.BLUE}
      isFilled
      iconType={IconType.PLUS}
      onClick={() => {
        popupFormManager.openForm(
          <WorkflowSelectionModal projectId={props.projectId} />,
        );
      }}
    />
  );
}

export default function AppliedWorkflowsBlock(props: { projectId: number }) {
  const projectPlanService = useProjectPlanService();

  const getAppliedPlans = async () => {
    const plans = await projectPlanService.getProjectPlans(props.projectId);
    return plans.filter(
      (plan): plan is AppliedProjectPlan => plan.appliedAt != null,
    );
  };

  return (
    <>
      <Separator />
      <SimpleBlock>
        <div className={styles.header}>
          <Header4>Workflows</Header4>
          <AddWorkflowButton projectId={props.projectId} />
        </div>
        <List
          getDataFunc={getAppliedPlans}
          updateOnEvents={[ADDITIONAL_PROJECT_PLAN_APPLIED, ADDITIONAL_PROJECT_PLAN_CREATED]}
        >
          <RowRepeater idField="id" blockClassName={styles.list} noDataMessage=" ">
            <AppliedWorkflowCard />
          </RowRepeater>
        </List>
      </SimpleBlock>
    </>
  );
}
